import React, { useState } from 'react';
import { Check, X, ArrowRight, Clock, Target, Shield, BarChart3, Users, Layers, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { CampaignSelector } from '../components/CampaignSelector';
const fmt = (n: number) => n.toLocaleString('en-US');
export function AdvantageProduct() {
  const [campaignMode, setCampaignMode] = useState(1);
  const [altBonus, setAltBonus] = useState(false);
  const isPartial = campaignMode === 0;
  const campaigns = isPartial ? 1 : campaignMode;
  const buildFee = isPartial ? 6000 : 6500 + 1000 * (campaignMode - 1);
  const systemFee = 2500;
  const campaignFee = isPartial ? 1250 : 2500 * campaigns;
  const monthlyTotal = systemFee + campaignFee;
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-[#7FE0C3]/30 font-sans" data-id="element-420">
      {/* 1. HERO */}
      <section className="relative pt-32 pb-8 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-421">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-[#7FE0C3]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-422" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-423">
          <div className="inline-block px-3 py-1 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 text-[#7FE0C3] text-xs font-bold uppercase tracking-widest mb-6" data-id="element-424">
            Flagship Partnership
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" data-id="element-425">
            HIRE Advantage
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed" data-id="element-426">
            HIRE Advantage is the partnership where recruiting becomes
            consistent, predictable, and scalable.
          </p>
        </motion.div>
      </section>

      {/* 2. PRICING */}
      <section className="pt-4 pb-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-427">
        <CampaignSelector mode={campaignMode} onChange={setCampaignMode} accent="mint" data-id="element-428" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-429">
          {/* Build Fee */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl relative overflow-hidden" data-id="element-430">
            <div className="mb-4" data-id="element-431">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-432">
                Build and Implementation (One-Time)
              </div>
              <div className="text-3xl font-bold text-white" data-id="element-433">
                ${fmt(buildFee)}
              </div>
              <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-434">
                {['Company and opportunity profiling', 'Custom ATS with dashboards, workflows, and job board integrations', 'Culture video and ad copywriting', 'Landing pages and candidate application flows', 'Full funnel design, from ad to onboarding', 'Compensation and role benchmarking by region', 'Pre-launch system stress test', 'Team training and 90-day coaching to optimize output'].map((point, i) => <li key={i} className="flex items-start" data-id="element-435">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-436" />
                    <span className="text-slate-400 text-sm leading-snug" data-id="element-437">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Monthly Total */}
          <div className="bg-slate-900/40 border border-[#7FE0C3]/30 p-6 rounded-xl relative" data-id="element-438">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-439" />
            <div className="mb-4" data-id="element-440">
              <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-2" data-id="element-441">
                Total Monthly
              </div>
              <div className="text-4xl font-bold text-white mb-1" data-id="element-442">
                ${fmt(monthlyTotal)}
              </div>
              <div className="text-sm text-slate-500 mb-6" data-id="element-443">/ month</div>

              <div className="space-y-3 pt-4 border-t border-slate-800/50" data-id="element-444">
                <div className="flex justify-between text-sm" data-id="element-445">
                  <span className="text-slate-400" data-id="element-446">System Fee</span>
                  <span className="text-white font-medium" data-id="element-447">
                    ${fmt(systemFee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-448">
                  <span className="text-slate-400" data-id="element-449">
                    {isPartial ? 'Partial Campaign' : campaigns === 1 ? 'Campaign Management' : `Campaign Management (x${campaigns})`}
                  </span>
                  <span className="text-white font-medium" data-id="element-450">
                    ${fmt(campaignFee)}
                  </span>
                </div>
              </div>

              <ul className="mt-5 space-y-2 pt-4 border-t border-slate-800/50" data-id="element-451">
                {['Active ad management and real-time optimization', 'Live applicant screening, 14 hrs/day, 7 days/week', 'Two-day sales school for every new hire', 'Interview auditing and coaching to improve retention', 'Monthly recruiting review and ongoing consulting'].map((point, i) => <li key={i} className="flex items-start" data-id="element-452">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-453" />
                    <span className="text-slate-400 text-sm leading-snug" data-id="element-454">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Performance Bonuses */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-455">
            <div className="mb-4" data-id="element-456">
              <button type="button" onClick={() => setAltBonus(v => !v)} aria-pressed={altBonus} title="Click to toggle bonus structure" className="text-xs text-amber-400 uppercase tracking-widest mb-4 cursor-pointer hover:text-amber-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 rounded" data-id="element-457">
                Performance Bonuses
              </button>
              <ul className="space-y-3" data-id="element-458">
                {(altBonus ? [{
                amount: '$500',
                label: 'per rep at 15 days'
              }, {
                amount: '$500',
                label: 'per rep at 45 days'
              }] : [{
                amount: '$250',
                label: 'per rep at launch'
              }, {
                amount: '$250',
                label: 'per rep at 30 days'
              }, {
                amount: '$250',
                label: 'per rep at 60 days'
              }, {
                amount: '$250',
                label: 'per rep at 90 days'
              }]).map((bonus, i) => <li key={i} className="flex items-start" data-id="element-459">
                    <span className="text-amber-400 mr-3 text-xl font-bold" data-id="element-460">
                      {bonus.amount}
                    </span>
                    <span className="text-slate-300 text-lg" data-id="element-461">
                      {bonus.label}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-5 pt-4 border-t border-slate-800/50 text-slate-400 text-sm leading-snug" data-id="element-462">
                Tied to retention milestones, not just placement. We only win
                when your reps stick.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 p-6 bg-[#7FE0C3]/5 border border-[#7FE0C3]/20 rounded-lg max-w-3xl mx-auto" data-id="element-463">
          <p className="text-[#7FE0C3] text-center font-medium" data-id="element-464">
            Ad spend is paid directly to the job platforms and is separate from
            HIRE's fees.
          </p>
        </div>
      </section>

      {/* 2. PROBLEM REFRAME */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto border-t border-slate-800/50" data-id="element-465">
        <div className="text-center mb-12" data-id="element-466">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-467">
            The Real Problem
          </h2>
          <p className="text-slate-400 text-lg" data-id="element-468">
            Most companies don't fail at finding candidates. They fail at system
            execution.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-469">
          <div className="bg-slate-900/30 border border-slate-800 p-8 rounded-xl" data-id="element-470">
            <h3 className="text-sm text-rose-400 uppercase tracking-widest font-bold mb-6" data-id="element-471">
              System Failures
            </h3>
            <ul className="space-y-4" data-id="element-472">
              {['Inconsistent interviews', 'Drifting hiring standards', 'Early churn discovered too late', 'Leaders relying on gut decisions'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-473">
                  <X className="w-5 h-5 text-rose-500/50 mr-3 mt-0.5 flex-shrink-0" data-id="element-474" />
                  <span className="line-through opacity-70" data-id="element-475">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="flex flex-col justify-center p-8" data-id="element-476">
            <p className="text-xl text-white font-medium mb-4" data-id="element-477">
              Advantage solves the system failure, not just the talent shortage.
            </p>
            <p className="text-slate-400" data-id="element-478">
              We replace guesswork with infrastructure and accountability.
            </p>
          </div>
        </div>
      </section>

      {/* 3. WHAT ADVANTAGE IS */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-479">
        <div className="max-w-5xl mx-auto" data-id="element-480">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-481">
            <div data-id="element-482">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-483">
                Full System Ownership
              </h2>
              <div className="space-y-4 text-slate-400 mb-8" data-id="element-484">
                <p data-id="element-485">This is not lead generation.</p>
                <p data-id="element-486">This is not outsourced recruiting.</p>
                <p data-id="element-487">This is full ownership of the recruiting system.</p>
              </div>
              <div className="p-6 bg-[#7FE0C3]/5 border border-[#7FE0C3]/20 rounded-lg" data-id="element-488">
                <p className="text-[#7FE0C3] font-medium" data-id="element-489">
                  HIRE Advantage is the only partnership level where HIRE is
                  accountable for the recruiting system actually working.
                </p>
              </div>
            </div>
            <div className="space-y-6" data-id="element-490">
              <div className="flex items-start" data-id="element-491">
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-492">
                  <Shield className="w-6 h-6 text-[#7FE0C3]" data-id="element-493" />
                </div>
                <div data-id="element-494">
                  <h3 className="text-white font-bold mb-2" data-id="element-495">Accountability</h3>
                  <p className="text-slate-400 text-sm" data-id="element-496">
                    We don't just deliver leads; we own the outcome of the
                    system's performance.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-497">
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-498">
                  <Target className="w-6 h-6 text-[#7FE0C3]" data-id="element-499" />
                </div>
                <div data-id="element-500">
                  <h3 className="text-white font-bold mb-2" data-id="element-501">Consistency</h3>
                  <p className="text-slate-400 text-sm" data-id="element-502">
                    Eliminating the variance between good weeks and bad weeks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. 10-STEP SYSTEM */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-503">
        <div className="text-center mb-12" data-id="element-504">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-505">
            The 10-Step Recruiting System
          </h2>
          <p className="text-slate-400" data-id="element-506">
            Every HIRE Advantage partner runs the same system. The difference is
            who owns it.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-507">
          {['Attract', 'Engage', 'Screen', 'Qualify', 'Commit', 'Schedule', 'Launch', 'Integrate', 'Train', 'Retain'].map((step, idx) => <div key={idx} className="bg-slate-900/40 border border-slate-800 p-6 rounded-lg text-center hover:border-[#7FE0C3]/30 transition-colors group" data-id="element-508">
              <div className="text-xs text-slate-500 mb-2 font-mono" data-id="element-509">
                0{idx + 1}
              </div>
              <div className="text-white font-medium group-hover:text-[#7FE0C3] transition-colors" data-id="element-510">
                {step}
              </div>
            </div>)}
        </div>
      </section>

      {/* 5. OWNERSHIP MAP */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-511">
        <div className="max-w-6xl mx-auto" data-id="element-512">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-513">
            Ownership Map
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-514">
            {/* HIRE Owns */}
            <div className="bg-slate-900/40 border border-[#7FE0C3]/20 rounded-xl p-8 relative overflow-hidden" data-id="element-515">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3]" data-id="element-516" />
              <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-517">
                <span className="w-2 h-2 rounded-full bg-[#7FE0C3] mr-3" data-id="element-518" />
                HIRE Owns
              </h3>
              <ul className="space-y-4" data-id="element-519">
                {['Recruiting system architecture (custom ATS)', 'Traffic, screening, and qualification standards', 'Hiring consistency and interview structure', 'Sales School and launch readiness', 'Early performance tracking and correction', 'Ongoing optimization and accountability'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-520">
                    <Check className="w-5 h-5 text-[#7FE0C3] mr-3 mt-0.5 flex-shrink-0" data-id="element-521" />
                    <span data-id="element-522">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* You Own */}
            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 relative overflow-hidden" data-id="element-523">
              <div className="absolute top-0 left-0 w-full h-1 bg-slate-600" data-id="element-524" />
              <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-525">
                <span className="w-2 h-2 rounded-full bg-slate-600 mr-3" data-id="element-526" />
                You Own
              </h3>
              <ul className="space-y-4" data-id="element-527">
                {['Final hiring decisions', 'Day-to-day sales leadership', 'Culture and performance expectations'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-528">
                    <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center" data-id="element-529">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-500" data-id="element-530" />
                    </div>
                    <span data-id="element-531">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 6. TIMELINE */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-532">
        <h2 className="text-3xl font-bold text-white mb-16 text-center" data-id="element-533">
          Partnership Timeline
        </h2>

        <div className="relative" data-id="element-534">
          {/* Horizontal connector line (desktop only) */}
          <div className="hidden md:block absolute top-3 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-[#7FE0C3]/30 to-transparent" data-id="element-535" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-536">
            {[{
            days: 'Days 1–21',
            title: 'Build & Launch',
            items: ['System architecture build', 'Campaign launch', 'Sales School setup', 'First candidates generated']
          }, {
            days: 'Weeks 4–12',
            title: 'Support & Consulting',
            items: ['Weekly support and consulting', 'Establishing strong baseline KPIs', 'Interviewing 30+ triple screened prospects/mo', 'Hiring 15+ people/mo']
          }, {
            days: 'Month 3+',
            title: 'Optimization',
            items: ['Increase conversion rates', 'Improve retention', 'Maximize ROI', 'Scale successful patterns']
          }].map((phase, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-537">
                {/* Timeline dot */}
                <div className="hidden md:flex items-center justify-center w-6 h-6 rounded-full bg-[#0A0F1C] border-2 border-[#7FE0C3] z-10 mb-8" data-id="element-538">
                  <div className="w-2 h-2 rounded-full bg-[#7FE0C3]" data-id="element-539" />
                </div>

                <div className="w-full bg-slate-900/30 border border-slate-800 p-6 rounded-xl text-center md:text-left" data-id="element-540">
                  <div className="text-[#7FE0C3] font-bold text-sm uppercase tracking-widest mb-2" data-id="element-541">
                    {phase.days}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4" data-id="element-542">
                    {phase.title}
                  </h3>
                  <ul className="space-y-2" data-id="element-543">
                    {phase.items.map((item, i) => <li key={i} className="text-slate-400 text-sm flex items-start justify-center md:justify-start" data-id="element-544">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-700 mr-2 mt-1.5 flex-shrink-0" data-id="element-545" />
                        <span data-id="element-546">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. WHO THIS IS FOR */}
      <section className="py-20 px-6 md:px-12 max-w-5xl mx-auto" data-id="element-547">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-548">
          <div data-id="element-549">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-550">
              <Check className="w-5 h-5 text-[#7FE0C3] mr-2" data-id="element-551" />
              This is for teams who:
            </h3>
            <ul className="space-y-4" data-id="element-552">
              {['Want recruiting to actually work', 'Value consistency over heroics', 'Are open to better systems', 'Want a partner, not a vendor'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-553">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7FE0C3] mr-3 mt-2" data-id="element-554" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div data-id="element-555">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-556">
              <X className="w-5 h-5 text-rose-500 mr-2" data-id="element-557" />
              This is not for teams who:
            </h3>
            <ul className="space-y-4" data-id="element-558">
              {['Only want resumes', "Aren't open to process", 'Want zero accountability'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-559">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-3 mt-2" data-id="element-560" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 9. HOW THIS SCALES */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800 text-center" data-id="element-561">
        <div className="max-w-3xl mx-auto" data-id="element-562">
          <h2 className="text-2xl font-bold text-white mb-8" data-id="element-563">
            How This Scales
          </h2>
          <div className="flex flex-col md:flex-row justify-center gap-6 text-left" data-id="element-564">
            <div className="flex-1 p-6 border border-[#7FE0C3]/30 rounded-lg bg-slate-800/30" data-id="element-565">
              <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-2" data-id="element-566">
                Foundation
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-567">Advantage</h3>
              <p className="text-slate-400 text-sm" data-id="element-568">
                The foundation your recruiting runs on.
              </p>
            </div>
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-569">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-570">
                Execution
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-571">Summit</h3>
              <p className="text-slate-400 text-sm" data-id="element-572">
                Exists when execution capacity is the bottleneck.
              </p>
            </div>
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-573">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-574">
                Leadership
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-575">Leadership</h3>
              <p className="text-slate-400 text-sm" data-id="element-576">
                Add it when finding leadership becomes the bottleneck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 10. CTA */}
      <section className="py-32 px-6 md:px-12 text-center" data-id="element-577">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-578">
          Ready to make recruiting predictable?
        </h2>
        <button className="inline-flex items-center px-8 py-4 bg-[#7FE0C3] hover:bg-[#5ACCA8] text-[#163A5F] font-bold rounded-lg transition-all transform hover:scale-105" data-id="element-579">
          Let's see if Advantage is the right fit
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-580" />
        </button>
      </section>
    </div>;
}